/**
 * MumSpineJourney · public one-tap Spinal Ease for Mum.
 *
 * Same healing engine as the authenticated Spinal Ease flow — just
 * wrapped in a public no-login shell tailored for a technophobe:
 *
 *   1. Landing card       → single warm "Begin" button
 *   2. Family Circle      → tap a face (Daisy · Charlie · Mia · Michael)
 *   3. Immersion          → the actual Cathedral-of-Light healing
 *   4. Softness afterward → "Rest. The light stays with you." + "Again"
 *
 * Works beautifully on iPhone and iPad (portrait & landscape).
 * Add-to-Home-Screen ready · warm gold icon.
 */
import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import HealingImmersion from "../HealingSupport/HealingImmersion";
import FamilyCircleTile from "./FamilyCircleTile";

const API = process.env.REACT_APP_BACKEND_URL;

// The 6 canonical family voices — hard-coded so this page never
// depends on an authenticated /family/voices endpoint.
const FAMILY_VOICES = [
  { id: "551d4cf818f246ee",         speaker_name: "Daisy",   relation: "Granddaughter", accent: "#F6C89C" },
  { id: "6568e5e2cd934487",         speaker_name: "Charlie", relation: "Grandson",      accent: "#C79B7C" },
  { id: "d51ff628141c4db4",         speaker_name: "Mia",     relation: "Granddaughter", accent: "#E9BFA8" },
  { id: "millie2026healingvoice02", speaker_name: "Millie",  relation: "Granddaughter", accent: "#F0CDA8" },
  { id: "janehealingvoice01",       speaker_name: "Jane",    relation: "Daughter",      accent: "#E6B489" },
  { id: "e52edcec30bb40a2",         speaker_name: "Michael", relation: "Son",           accent: "#D9AB6E" },
];

export default function MumSpineJourney() {
  // idle → circle → session → complete
  const [phase, setPhase] = useState("idle");
  const [selectedVoice, setSelectedVoice] = useState(null);

  // Immersive body flag + iPad-friendly meta tags
  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    document.body.dataset.immersive = "true";
    const prevTitle = document.title;
    document.title = "Spinal Ease · Mum";

    const added = [];
    const setMeta = (attrs) => {
      const el = document.createElement("meta");
      Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
      document.head.appendChild(el);
      added.push(el);
    };
    const setLink = (attrs) => {
      const el = document.createElement("link");
      Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
      document.head.appendChild(el);
      added.push(el);
    };
    setMeta({ name: "apple-mobile-web-app-capable",          content: "yes" });
    setMeta({ name: "apple-mobile-web-app-status-bar-style", content: "black-translucent" });
    setMeta({ name: "apple-mobile-web-app-title",            content: "Spine" });
    setMeta({ name: "theme-color",                           content: "#0a0a12" });
    setMeta({ name: "viewport",                              content: "width=device-width, initial-scale=1, viewport-fit=cover" });
    setLink({ rel: "apple-touch-icon",                       href: "/apple-touch-icon.png" });

    return () => {
      delete document.body.dataset.immersive;
      document.title = prevTitle;
      added.forEach((el) => { try { el.remove(); } catch (_) { /* noop */ } });
    };
  }, []);

  return (
    <div
      data-testid="mum-spine-journey"
      className="fixed inset-0 overflow-hidden"
      style={{ background: "radial-gradient(circle at 50% 45%, #0f1420 0%, #06080d 55%, #030307 100%)" }}
    >
      <AnimatePresence mode="wait">
        {phase === "idle" && (
          <IdleScreen key="idle" onStart={() => setPhase("circle")} />
        )}
        {phase === "circle" && (
          <FamilyCircleScreen
            key="circle"
            voices={FAMILY_VOICES}
            onPick={(v) => {
              try {
                fetch(`${API}/api/family-journey/pick`, {
                  method: "POST",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({
                    recipient: "nanna",
                    page: "spine",
                    voice_id: v.id,
                    speaker_name: v.speaker_name,
                  }),
                }).catch(() => {});
              } catch (_) { /* noop */ }
              setSelectedVoice(v); setPhase("session");
            }}
          />
        )}
        {phase === "session" && selectedVoice && (
          <motion.div
            key="session"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1.2 }}
            className="fixed inset-0"
          >
            <HealingImmersion
              condition="spinal-ease"
              voiceOverride={{
                url: `${API}/api/healing/spinal-ease/family/voices/${selectedVoice.id}/audio`,
                speaker_name: selectedVoice.speaker_name,
              }}
              publicMode
              keepsakeMeta={{
                recipient:    "nanna",
                page:         "spine",
                voice_id:     selectedVoice.id,
                speaker_name: selectedVoice.speaker_name,
              }}
              onComplete={() => setPhase("complete")}
            />
          </motion.div>
        )}
        {phase === "complete" && (
          <CompleteScreen key="complete" onAgain={() => setPhase("circle")} />
        )}
      </AnimatePresence>
    </div>
  );
}

/* ── Idle · Begin ─────────────────────────────────────────────── */
function IdleScreen({ onStart }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 1.0, ease: [0.22, 1, 0.36, 1] }}
      className="relative z-10 flex flex-col items-center justify-center gap-10 px-8 text-center min-h-screen"
    >
      <motion.div
        className="absolute inset-0 pointer-events-none"
        style={{ background: "radial-gradient(circle at 50% 55%, rgba(215, 187, 122, 0.14) 0%, transparent 62%)" }}
        animate={{ opacity: [0.7, 1, 0.7] }}
        transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
      />

      <div className="relative flex flex-col items-center gap-3">
        <div
          className="text-amber-100/80 tracking-[0.35em] uppercase text-[10px]"
          style={{ fontFamily: '"Cormorant Garamond", Georgia, serif' }}
        >
          Reclaim · Spinal Ease
        </div>
        <div
          className="text-amber-50 text-4xl sm:text-5xl leading-tight"
          style={{ fontFamily: '"Cormorant Garamond", Georgia, serif' }}
        >
          Welcome home, <span className="italic text-amber-200/95">Mum</span>
        </div>
        <div
          className="text-white/50 text-base max-w-sm mx-auto italic mt-3 leading-relaxed"
          style={{ fontFamily: '"Cormorant Garamond", Georgia, serif' }}
        >
          Put on headphones if you can.<br/>
          Lie back. One tap and you are there.
        </div>
      </div>

      <motion.button
        onClick={onStart}
        data-testid="mum-spine-begin"
        whileHover={{ scale: 1.03 }}
        whileTap={{ scale: 0.97 }}
        className="relative flex items-center justify-center rounded-full"
        style={{
          width: 240, height: 240,
          background: "radial-gradient(circle at 50% 45%, #f6d68a 0%, #d59f4f 45%, #8a5b26 100%)",
          boxShadow: "0 0 70px rgba(245, 200, 130, 0.55), 0 0 140px rgba(200, 130, 80, 0.35), inset 0 6px 26px rgba(255, 240, 200, 0.5), inset 0 -6px 26px rgba(120, 60, 30, 0.5)",
        }}
      >
        <motion.span
          className="absolute inset-0 rounded-full"
          style={{ border: "1px solid rgba(255, 224, 170, 0.55)" }}
          animate={{ scale: [1, 1.08, 1], opacity: [0.6, 0.15, 0.6] }}
          transition={{ duration: 3.4, repeat: Infinity, ease: "easeInOut" }}
        />
        <span
          className="text-[#3a1e08] text-3xl tracking-wide"
          style={{ fontFamily: '"Cormorant Garamond", Georgia, serif', fontWeight: 600, letterSpacing: "0.03em" }}
        >
          Begin
        </span>
      </motion.button>

      <div className="text-white/40 text-xs italic tracking-widest">
        20 minutes · Cathedral of Light
      </div>
    </motion.div>
  );
}

/* ── Family Circle · pick a voice ─────────────────────────────── */
function FamilyCircleScreen({ voices, onPick }) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
      className="relative z-10 flex flex-col items-center gap-6 px-8 pt-14 pb-16 text-center h-full overflow-y-auto"
    >
      <div className="flex flex-col items-center gap-2 shrink-0">
        <div
          className="text-amber-100/80 tracking-[0.35em] uppercase text-[10px]"
          style={{ fontFamily: '"Cormorant Garamond", Georgia, serif' }}
          data-testid="mum-spine-circle-title"
        >
          Choose your voice
        </div>
        <div
          className="text-amber-50 text-2xl sm:text-3xl italic"
          style={{ fontFamily: '"Cormorant Garamond", Georgia, serif' }}
        >
          who would you like to walk with you?
        </div>
      </div>

      <div className="grid grid-cols-2 gap-5 sm:gap-8 mt-2 shrink-0">
        {voices.map((v, i) => (
          <FamilyCircleTile
            key={v.id}
            voice={v}
            index={i}
            previewSrc={`${API}/api/healing/spinal-ease/family/voices/${v.id}/audio`}
            testIdPrefix="mum-spine-voice"
            onPick={onPick}
          />
        ))}
      </div>

      <div className="mt-4 text-white/35 text-xs italic tracking-widest">
        Tap a face — it takes you straight in
      </div>
    </motion.div>
  );
}

/* ── Complete · softness afterward ────────────────────────────── */
function CompleteScreen({ onAgain }) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 1.6, ease: [0.22, 1, 0.36, 1] }}
      className="relative z-10 flex flex-col items-center justify-center gap-8 px-8 text-center min-h-screen"
    >
      <div className="flex flex-col items-center gap-3">
        <div
          className="text-amber-50 text-3xl sm:text-4xl italic leading-tight"
          style={{ fontFamily: '"Cormorant Garamond", Georgia, serif' }}
        >
          Rest, <span className="text-amber-200/95">Mum</span>
        </div>
        <div
          className="text-white/50 text-base max-w-sm mx-auto italic mt-2"
          style={{ fontFamily: '"Cormorant Garamond", Georgia, serif' }}
        >
          The light stays with you.
        </div>
      </div>
      <motion.button
        onClick={onAgain}
        data-testid="mum-spine-again"
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        className="text-amber-100/80 text-sm tracking-[0.25em] uppercase border-b border-amber-100/25 pb-1 hover:text-amber-100"
      >
        Again
      </motion.button>
    </motion.div>
  );
}
